/**
 * Navigation feature types.
 */

import React from 'react';
import {
    HomeOutlined, UserOutlined, BankOutlined, TeamOutlined,
    GlobalOutlined, FileTextOutlined, HomeFilled, CreditCardOutlined,
    SafetyOutlined, CheckCircleOutlined, FormOutlined, BookOutlined,
    SettingOutlined, ShopOutlined, TrophyOutlined, BulbOutlined,
    SearchOutlined
} from '@ant-design/icons';

// ─── Navigation Item ─────────────────────────────────────────────────────────

export interface NavItem {
    path: string;
    label: string;
    description: string;
    category: NavCategory;
    icon: React.ReactNode;
    keywords: string[];
    requiredPermission?: string;
}

export type NavCategory =
    | 'dashboard'
    | 'customers'
    | 'institutions'
    | 'users'
    | 'banking'
    | 'loans'
    | 'mortgages'
    | 'payments'
    | 'compliance'
    | 'kyc'
    | 'audit'
    | 'general-ledger'
    | 'settings'
    | 'branch'
    | 'rewards'
    | 'insights'
    | 'search';

export const NAV_CATEGORY_LABELS: Record<NavCategory, string> = {
    dashboard: 'Dashboard',
    customers: 'Customers',
    institutions: 'Institutions',
    users: 'Users',
    banking: 'Banking & Open Banking',
    loans: 'Loans',
    mortgages: 'Mortgages',
    payments: 'Payment Operations',
    compliance: 'Compliance',
    kyc: 'KYC Management',
    audit: 'Audit',
    'general-ledger': 'General Ledger',
    settings: 'Settings & Admin',
    branch: 'Branch Banking',
    rewards: 'Rewards',
    insights: 'Insights',
    search: 'Search',
};

export const NAV_CATEGORY_ICONS: Record<NavCategory, React.ReactNode> = {
    dashboard: React.createElement(HomeOutlined),
    customers: React.createElement(UserOutlined),
    institutions: React.createElement(BankOutlined),
    users: React.createElement(TeamOutlined),
    banking: React.createElement(GlobalOutlined),
    loans: React.createElement(FileTextOutlined),
    mortgages: React.createElement(HomeFilled),
    payments: React.createElement(CreditCardOutlined),
    compliance: React.createElement(SafetyOutlined),
    kyc: React.createElement(CheckCircleOutlined),
    audit: React.createElement(FormOutlined),
    'general-ledger': React.createElement(BookOutlined),
    settings: React.createElement(SettingOutlined),
    branch: React.createElement(ShopOutlined),
    rewards: React.createElement(TrophyOutlined),
    insights: React.createElement(BulbOutlined),
    search: React.createElement(SearchOutlined),
};

// ─── Quick Actions ───────────────────────────────────────────────────────────

export interface QuickAction {
    id: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    path?: string;
    action?: string;
    keywords: string[];
}

// ─── Activity Tracking ───────────────────────────────────────────────────────

export interface RecentPage {
    path: string;
    label: string;
    category: NavCategory;
    visitCount: number;
    lastVisited: number;
}

export interface FavouritePage {
    path: string;
    label: string;
    category: NavCategory;
    addedAt: number;
}

// ─── Command Palette Result ──────────────────────────────────────────────────

export type PaletteResultType = 'page' | 'action' | 'recent' | 'favourite';

export interface PaletteResult {
    type: PaletteResultType;
    id: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    category: NavCategory;
    path?: string;
    action?: string;
    meta?: string;
}

// ─── AI Chat ─────────────────────────────────────────────────────────────────

export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    action?: NavigationAction;
}

export interface NavigationAction {
    type: 'navigate' | 'filter' | 'open' | 'info';
    path?: string;
    params?: Record<string, string>;
    description: string;
}
